import { AppStateStatus } from "~/ui/primitives"
import {
  flow,
  applySnapshot,
  Instance,
  SnapshotOut,
  types,
  onSnapshot,
  toGenerator,
} from "mobx-state-tree"

import { PopupStoreModel } from "../popup-store"
import { WebsocketStoreModel } from "../websocket-store"
import { AuthStoreModel } from "../auth-store"
import { WalletStoreModel } from "../wallet-store"
import { BankStoreModel } from "../bank-store"
import { OrderStoreModel } from "../order-store"
import { GlobalToastStoreModel } from "../global-toast-store"
import { GlobalModalStoreModel } from "../global-modal-store"

import { EventStoreModel } from "../event-store"
import { MessageStoreModel } from "../message-store"
import { ChatStoreModel } from "../chat-store"
import { UserStoreModel } from "../user-store"
import { ContactStoreModel } from "../contact-store"
import { ChannelStoreModel } from "../channel-store"
import { ConfigStoreModel } from "../config-store"
import { FriendStoreModel } from "../friend-store"
import { APIStoreModel } from "../api-store"
import { BlackListStoreModel } from "../black-list-store"
import { SettingStoreModel } from "../setting-store"
import { NotificationStoreModel } from "../notification-store"
import * as storage from "~utils/storage"
import { getPersistStoreValues, rootStoreInstance, storageDataMerge } from "./setup-root-store"
import { dropMessages, queryMessagesByChatIdentity } from "~services/sqlite"
import { Event, EventBatchPackedData, EventNames, EventPackedData } from "~proto"
import { USER_STATE_STORAGE_KEY } from "~utils/storage/storage-keys"
import config from "~config/env"

/**
 * 全量持久化前剔除「消息」相关的大字段：messageStore.messages（完整消息对象）与各会话的 messages（消息 id 列表）。
 * 这些数据已存于 SQLite，load() 重启时会从 SQLite 重建（queryMessagesByChatID），留在 AsyncStorage 快照里既冗余，
 * 又是每次保存时同步 JSON.stringify 的主要开销——未读消息多时每帧都要序列化上万条消息，会阻塞主线程造成卡顿。
 */
function slimSnapshotForPersist(snapshot: any) {
  const next = { ...snapshot }
  // 认证 token 严禁写入用户快照，避免刷新时“旧 token 回填”导致自动登录复活
  if (next.authStore) {
    next.authStore = { ...next.authStore, token: "" }
  }
  if (next.messageStore) {
    next.messageStore = { ...next.messageStore, messages: {} }
  }
  if (next.chatStore?.chats) {
    const chats = next.chatStore.chats
    const slimChats: Record<string, any> = {}
    for (const id in chats) {
      slimChats[id] = { ...chats[id], messages: [] }
    }
    next.chatStore = { ...next.chatStore, chats: slimChats }
  }
  return next
}

/**
 * A RootStore model.
 */
export const RootStoreModel = types
  .model("RootStore")
  .props({
    popupStore: types.optional(PopupStoreModel, {}),
    configStore: types.optional(ConfigStoreModel, {} as any),
    authStore: types.optional(AuthStoreModel, {} as any),
    walletStore: types.optional(WalletStoreModel, {} as any),
    orderStore: types.optional(OrderStoreModel, {} as any),
    websocketStore: types.optional(WebsocketStoreModel, {} as any),
    channelStore: types.optional(ChannelStoreModel, {} as any),
    userStore: types.optional(UserStoreModel, {} as any),
    eventStore: types.optional(EventStoreModel, {} as any),
    contactStore: types.optional(ContactStoreModel, {} as any),
    chatStore: types.optional(ChatStoreModel, {} as any),
    messageStore: types.optional(MessageStoreModel, {} as any),
    friendStore: types.optional(FriendStoreModel, {} as any),
    apiStore: types.optional(APIStoreModel, {} as any),
    blackListStore: types.optional(BlackListStoreModel, {} as any),
    bankStore: types.optional(BankStoreModel, {} as any),
    settingStore: types.optional(SettingStoreModel, {} as any),
    globalToastStore: types.optional(GlobalToastStoreModel, {} as any),
    globalModalStore: types.optional(GlobalModalStoreModel, {} as any),
    notificationStore: types.optional(NotificationStoreModel, {} as any),
  })
  .volatile(() => ({
    onSnapshotDisposer: undefined,
    appState: "active",
    syncFinished: false,
  }))
  .actions((self) => {
    const setSyncFinished = (v: boolean) => {
      self.syncFinished = v
    }
    const onSyncFinished = () => {
      setSyncFinished(true)
    }
    /**
     * EVENT_PACKED(82) 批量下发未读消息（每帧约 20 条，可达数千条）。逐条 dispatch 时每条
     * MESSAGE_CREATED 都会各起 flow（SQLite insert / 回复链）并逐条 unshift 渲染列表，几千条时
     * 会阻塞触摸与掉帧。这里把「连续的 MESSAGE_CREATED」聚合成一批交给 messageStore 批处理，
     * 其余事件仍按原顺序逐条 dispatch（保持顺序，避免与穿插的状态类事件错位）。
     */
    const onEventPacked = ({ data }: Event<EventBatchPackedData>) => {
      const { items } = data
      if (!items?.length) return

      let pendingMessages: EventPackedData["data"][] = []
      const flushMessages = () => {
        if (!pendingMessages.length) return
        self.messageStore.onPackedMessagesCreated(pendingMessages as any)
        pendingMessages = []
      }

      items.forEach((packedData) => {
        if (packedData.event_name === EventNames.MESSAGE_CREATED) {
          pendingMessages.push(packedData.data)
          return
        }
        flushMessages()
        self.eventStore.onEvent(new Event(packedData.event_name, packedData.data))
      })

      flushMessages()
    }
    function setAppState(state: AppStateStatus) {
      self.appState = state
    }
    function setOnSnapshotDisposer(disposer) {
      self.onSnapshotDisposer = disposer
    }
    const clearCache = flow(function* () {
      yield storage.remove(USER_STATE_STORAGE_KEY)
      yield dropMessages()
    })
    // 重置状态
    const reset = flow(function* () {
      yield self.authStore.setToken("")
      const persistData = yield getPersistStoreValues()
      const data = storageDataMerge(persistData, {})
      self.onSnapshotDisposer && self.onSnapshotDisposer()
      applySnapshot(self, data)
      yield clearCache()
    })
    const load = flow(function* () {
      try {
        const persistData = yield getPersistStoreValues()
        const userData = yield storage.load(USER_STATE_STORAGE_KEY)
        const data = storageDataMerge(persistData, userData || {})
        if (data?.chatStore?.chats) {
          let allMessages = []
          const chat_ids = Object.keys(data.chatStore.chats)
          for (let i = 0; i < chat_ids.length; i++) {
            const chat_id = chat_ids[i]
            const chat = data.chatStore.chats[chat_id]
            const result = yield* toGenerator(
              queryMessagesByChatIdentity(
                { chat_id, id: chat.id, name: chat.name },
                0,
              ),
            )
            chat.messages = result.map((m) => m.message_id)
            allMessages = allMessages.concat(result)
          }
          if (!data?.messageStore) {
            data.messageStore = {
              messages: {},
            }
          }
          for (let j = 0; j < allMessages.length; j++) {
            const message = allMessages[j]
            const { message_id } = message
            data.messageStore.messages[message_id] = message
          }
        }
        applySnapshot(rootStoreInstance, data)

        const { configStore } = rootStoreInstance
        if (configStore.buildEnv !== config.ENV_NAME || configStore.api !== config.API_URL) {
          configStore.applyEnvDefaults()
        }

        const disposer = onSnapshot(rootStoreInstance, (snapshot) => {
          storage.save(USER_STATE_STORAGE_KEY, slimSnapshotForPersist(snapshot))
        })
        setOnSnapshotDisposer(disposer)
      } catch (e) {
      }
    })
    return { setSyncFinished, onSyncFinished, onEventPacked, setAppState, reset, load }
  })

/**
 * The RootStore instance.
 */
export interface RootStore extends Instance<typeof RootStoreModel> {}

/**
 * The data of a RootStore.
 */
export interface RootStoreSnapshot extends SnapshotOut<typeof RootStoreModel> {}
